import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "../axios";

// The two sources behind the project editor's "Colours used" picker, both from one action on
// MemberProfileApiController: a free-text search over every published chip, and the member's own
// saved colours. Route template is umbraco/api/[controller]/[action], and the [controller] token
// drops the "Controller" suffix.
const ENDPOINT = "/umbraco/api/MemberProfileApi/Colours/";

/**
 * One page of colours.
 *
 * Answers in one shape whichever source it read, because the picker paints both with the same
 * grid: an array of { key, name, code, rgb }.
 *
 *   key   the chip's content GUID. THIS is what coloursUsed stores and what the form posts back.
 *   code  the colour chip code, e.g. R157C. What every other colour surface on the site is keyed
 *         by, and what the member reads under the name.
 *   rgb   a ready-to-use CSS colour, already wrapped as rgb(r, g, b).
 *
 * The server has the last word on both the page size and the minimum term length. The settings
 * handed to the form carry the same two numbers, so it can stop asking pointless questions — but
 * it is not the thing enforcing them.
 */
export const fetchColours = async ({ term, saved }, { signal } = {}) => {
  const response = await axiosInstance.get(ENDPOINT, {
    // Sent as params rather than interpolated so a term carrying & or # is escaped for us.
    params: saved ? { saved: true } : { term },
    signal,
  });

  return response.data?.items ?? [];
};

/**
 * The picker's query.
 *
 * `enabled` is the gate: nothing is fetched until the source is actually askable — the saved list
 * whenever the picker is open on it, a search only once the term is at least `minLength`. That
 * number comes from the editor settings, which come from appsettings, so the client gate and the
 * server gate are the same number and a member is never shown a spinner for a request the server
 * was always going to answer empty.
 *
 * `signal` is passed to axios so a term typed over cancels the request it replaced instead of
 * racing it — without it a slow "blu" can land after "blue" and paint the wrong results.
 */
export const useColours = ({ term, saved, minLength, enabled = true }) => {
  const trimmed = (term ?? "").trim();
  const isAskable = saved || trimmed.length >= minLength;

  return useQuery({
    // Array key, with the source in it: switching between search and saved colours reads a
    // different cache entry rather than briefly showing the other source's results.
    queryKey: ["member-profile-colours", saved ? "saved" : "search", saved ? "" : trimmed],
    queryFn: ({ signal }) => fetchColours({ term: trimmed, saved }, { signal }),
    enabled: enabled && isAskable,
  });
};
